import { BadgeV3, ButtonV2, Dropdown } from '@cimplico/components';
import { PlusCircleIcon } from '@heroicons/react/20/solid';
import { CreditCardIcon } from '@heroicons/react/24/outline';
import { useState } from 'react';
import { useDeletePaymentMethod } from '../../../../queries/billing/use-delete-payment-method';
import { PaymentMethod, usePaymentMethods } from '../../../../queries/billing/use-payment-methods';
import { useSetDefaultPaymentMethod } from '../../../../queries/billing/use-set-default-payment-method';
import { SubscriptionV2 } from '../../../../types/firm';
import MasterCard from './card-icons/mastercard.png';
import Visa from './card-icons/visa.png';
import LoadingCell from './LoadingCell';
import NewCardModal from './NewCardModal';

type PaymentSectionProps = {
    subscription: SubscriptionV2;
};

function PaymentSection({ subscription }: PaymentSectionProps): JSX.Element {
    const [addingCard, setAddingCard] = useState(false);
    const query = usePaymentMethods();
    const setDefaultQuery = useSetDefaultPaymentMethod();
    const removeQuery = useDeletePaymentMethod();
    const paymentMethods = query.data?.paymentMethods ?? [];

    function setDefault(pm: PaymentMethod): void {
        if (pm.isDefault) {
            return;
        }
        setDefaultQuery.mutate({ paymentMethodId: pm.id });
    }

    function remove(pm: PaymentMethod): void {
        if (pm.isDefault) {
            return;
        }
        removeQuery.mutate({ paymentMethodId: pm.id });
    }

    return (
        <>
            {addingCard && <NewCardModal firstPaymentMethod={paymentMethods.length === 0} onClose={(): void => setAddingCard(false)} />}
            <div className="flex gap-16 flex-wrap">
                <div className="w-[400px]">
                    <h3 className="text-contentPrimary font-bold">Payment Methods</h3>
                    <p className="text-sm text-contentSecondary mt-1">
                        Manage your saved payment methods. You can view, update, or replace the cards we’ll use to charge your subscription.
                    </p>
                </div>
                <div className="flex-1">
                    <div className="flex justify-between items-start">
                        <p className="text-contentPrimary text-sm font-bold">Cards</p>
                        <ButtonV2
                            size="sm"
                            label="Add card"
                            leftIcon={<PlusCircleIcon />}
                            type="secondary"
                            isDisabled={query.isLoading || Boolean(subscription.cancelAt) || Boolean(subscription.cancelledOn)}
                            onClick={(): void => setAddingCard(true)}
                        />
                    </div>

                    <table className="w-full mt-8">
                        <thead>
                            <tr className="border-b">
                                <th className="text-contentPrimary text-xs font-bold uppercase text-left pb-2">Provider</th>
                                <th className="text-contentPrimary text-xs font-bold uppercase text-left pb-2">Type</th>
                                <th className="text-contentPrimary text-xs font-bold uppercase text-left pb-2">Number (Last 4)</th>
                                <th className="text-contentPrimary text-xs font-bold uppercase text-left pb-2">Expiry</th>
                            </tr>
                        </thead>
                        <tbody>
                            {query.isLoading && (
                                <>
                                    <tr>
                                        <LoadingCell colSpan={4} />
                                    </tr>
                                    <tr>
                                        <LoadingCell colSpan={4} />
                                    </tr>
                                </>
                            )}
                            {paymentMethods.map((paymentMethod) => (
                                <tr key={paymentMethod.id} className="border-t first:border-0">
                                    <td className="text-contentSecondary text-sm py-2 capitalize">
                                        <div className="flex items-center gap-2">
                                            {paymentMethod.brand === 'visa' && <img src={Visa} alt="Visa" className="w-8" />}
                                            {paymentMethod.brand === 'mastercard' && (
                                                <img src={MasterCard} alt="Mastercard" className="w-8" />
                                            )}
                                            {paymentMethod.brand}
                                            {paymentMethod.isDefault && (
                                                <span className="shrink-0">
                                                    <BadgeV3 label="Default" color="indigo" />
                                                </span>
                                            )}
                                        </div>
                                    </td>
                                    <td className="text-contentSecondary text-sm py-2">Card</td>
                                    <td className="text-contentSecondary text-sm py-2">**** {paymentMethod.last4}</td>
                                    <td className="text-contentSecondary text-sm py-2">
                                        <div className="flex items-center justify-between">
                                            {paymentMethod.expMonth}/{paymentMethod.expYear}
                                            <Dropdown>
                                                <Dropdown.Option label="Set as default" onClick={(): void => setDefault(paymentMethod)} />
                                                <Dropdown.Option
                                                    label="Remove"
                                                    onClick={(): void => remove(paymentMethod)}
                                                    disabled={paymentMethod.isDefault}
                                                    disabledHint={
                                                        paymentMethod.isDefault
                                                            ? 'Cannot remove the default payment method. Set another as default before removing this one.'
                                                            : undefined
                                                    }
                                                />
                                            </Dropdown>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>

                    {paymentMethods.length === 0 && !query.isLoading && (
                        <div className="text-contentSecondary text-sm mt-6 flex flex-col items-center justify-center">
                            <CreditCardIcon className="h-8 w-8" />
                            <p>No payment methods found.</p>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
}

export default PaymentSection;
